import { api } from './api';

interface SystemPricingDetail {
  year: number;
  monthlyPayment: number;
  kwhRate: number;
  batteryPayment?: number;
  totalMonthlyPayment?: number;
  totalYearlyCost?: number;
  estimatedAnnualProduction?: number;
  guaranteedAnnualProduction?: number;
  yearlyCost?: number;
}

interface QuoteResponse {
  id: string;
  accountId?: string;
  productId: string;
  type?: string;
  kwhPrice: number;
  escalationRate?: number;
  totalAmountPaid: number;
  totalSystemCost: number;
  pricePerWatt: number;
  systemSizeKw?: number;
  systemFirstYearProductionKwh: number;
  systemPricingDetails: SystemPricingDetail[];
  status: string;
  contractId?: string;
  batteryDetails?: {
    type: string;
    pricingMethod?: string;
    manufacturer?: string;
    models?: Array<{ modelNumber: string; quantity: number }>;
  };
}

interface OfferSigner {
  name: string;
  email: string;
  hasAccepted: boolean;
}

interface OfferAddress {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

interface GetRepricedQuoteResponse {
  activeQuote: QuoteResponse | null;
  rePricedQuote: QuoteResponse | null;
  offerStatus: string;
  expiresAt: string | null;
  signers: OfferSigner[];
  address: OfferAddress | null;
  contractHtml: string | null;
}

export interface PreBillingAdjustmentOfferResponse {
  originalQuote: {
    kwhPrice: number;
    escalationRate: number;
    totalAmountPaid: number;
    pricingSettings: { agreementLength: number };
    systemPricingDetails: SystemPricingDetail[];
  } | null;
  adjustmentQuote: {
    kwhPrice: number;
    escalationRate: number;
    totalAmountPaid: number;
    systemPricingDetails: SystemPricingDetail[];
  } | null;
  offerStatus: string;
  expiresAt: string | null;
  signers: OfferSigner[];
  address: OfferAddress | null;
  contractHtml: string | null;
}

interface GetRepricedQuoteQueryParams {
  accountId: string;
  inviteToken: string | null;
}

const mapQuoteToOfferResponse = (response: GetRepricedQuoteResponse): PreBillingAdjustmentOfferResponse => ({
  originalQuote: response.activeQuote
    ? {
        kwhPrice: response.activeQuote.kwhPrice,
        escalationRate: response.activeQuote.escalationRate ?? 0,
        totalAmountPaid: response.activeQuote.totalAmountPaid,
        pricingSettings: {
          agreementLength: response.activeQuote.systemPricingDetails?.length ?? 25,
        },
        systemPricingDetails: response.activeQuote.systemPricingDetails ?? [],
      }
    : null,
  adjustmentQuote: response.rePricedQuote
    ? {
        kwhPrice: response.rePricedQuote.kwhPrice,
        escalationRate: response.rePricedQuote.escalationRate ?? 0,
        totalAmountPaid: response.rePricedQuote.totalAmountPaid,
        systemPricingDetails: response.rePricedQuote.systemPricingDetails ?? [],
      }
    : null,
  offerStatus: response.offerStatus,
  expiresAt: response.expiresAt,
  signers: response.signers,
  address: response.address,
  contractHtml: response.contractHtml,
});

export const preBillingAdjustmentOfferApi = api.injectEndpoints({
  endpoints: (build) => ({
    getRepricedQuoteOptions: build.query<PreBillingAdjustmentOfferResponse, GetRepricedQuoteQueryParams>({
      query: ({ accountId, inviteToken }) => ({
        url: `accounts/${accountId}/quotes/reprice`,
        params: inviteToken ? { inviteToken } : undefined,
      }),
      transformResponse: (response: GetRepricedQuoteResponse) => mapQuoteToOfferResponse(response),
      providesTags: () => [{ type: 'Quotes', id: 'REPRICE' }],
    }),
    acceptPreBillingAdjustment: build.mutation<
      void,
      { accountId: string; acceptedName: string; inviteToken: string | null }
    >({
      query: ({ accountId, acceptedName, inviteToken }) => ({
        url: `accounts/${accountId}/pre-billing-adjustment/accept`,
        method: 'POST',
        body: { acceptedName },
        params: inviteToken ? { inviteToken } : undefined,
      }),
      async onQueryStarted({ accountId, inviteToken }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            preBillingAdjustmentOfferApi.util.updateQueryData(
              'getRepricedQuoteOptions',
              { accountId, inviteToken },
              (draft) => {
                draft.offerStatus = 'accepted';
              },
            ),
          );
        } catch {
          // mutation failed — no cache update needed
        }
      },
    }),
  }),
});

export const { useGetRepricedQuoteOptionsQuery, useAcceptPreBillingAdjustmentMutation } =
  preBillingAdjustmentOfferApi;
